import type React from 'react';
import {useState} from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Skeleton, Stack, TextField,
    Typography,
} from '@mui/material';
import {usePlanHistoryQuery} from '../../hooks/useCategoryPlan';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});
const ALL = 'All sections';

type Props = {databagId: number; path: string; onClose: () => void};

/** Every change to the plan, newest first, from the audit log: who, when, which section and what changed. */
export const PlanHistoryDialog: React.FC<Props> = ({databagId, path, onClose}) => {
    const {data, isLoading, isError} = usePlanHistoryQuery(databagId, path, true);
    const [section, setSection] = useState(ALL);
    const sections = [ALL, ...new Set((data ?? []).map(e => e.section))];
    const shown = (data ?? []).filter(e => section === ALL || e.section === section);

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="plan-history-title">
            <DialogTitle id="plan-history-title">Change history</DialogTitle>
            <DialogContent dividers>
                <Stack gap={2}>
                    <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                            Every change made to this plan, by whom and when, newest first. Kept in the audit log.
                        </Typography>
                        <TextField select size="small" label="Section" value={section} sx={{minWidth: 200}}
                                   onChange={e => setSection(e.target.value)}>
                            {sections.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                    </Stack>
                    {isError && <Alert severity="error">Could not load the change history.</Alert>}
                    {isLoading && <Skeleton variant="rounded" height={240}/>}
                    {data && shown.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No changes yet: the plan is still the draft.</Typography>
                    )}
                    <Stack component="ol" gap={1.5} sx={{m: 0, p: 0, listStyle: 'none'}}>
                        {shown.map(e => (
                            <Box component="li" key={e.id} sx={{borderLeft: 2, borderColor: 'divider', pl: 1.5}}>
                                <Typography variant="caption" color="text.secondary">
                                    {`${WHEN.format(new Date(e.at))} · ${e.actor} · `}
                                    <b>{e.section}</b>
                                </Typography>
                                {e.lines.map((line, i) => (
                                    <Typography key={i} variant="body2" sx={{overflowWrap: 'anywhere'}}>{line}</Typography>
                                ))}
                            </Box>
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};
